import { useMemo } from 'react';
import { useUnit } from 'effector-react';
import { useTranslation } from 'react-i18next';
import { Controller, useFormContext } from 'react-hook-form';
import { $countryProvincesStore, $countriesStore, $recipientMandatoryFieldsStore } from '@lib/store';
import { cn, PaletteUtils, ValidateUtils } from '@shared/utils';
import { TransferRecipientTypeEnum } from '@shared/api';
import { BaseInput, Select, Text } from '@shared/widgets';

export interface AddressFormData {
  address: string; // insi_address
  postCode: string; // insi_addresspostcode'
  city: string; // insi_addresscity
  province: string; // insi_addressprovince
  companyCountry: string; // countryCode
}

interface AddressFormBlockProps {
  className?: string;
  type?: TransferRecipientTypeEnum;
}

export const AddressFormBlock = (props: AddressFormBlockProps) => {
  const { type = TransferRecipientTypeEnum.Individual } = props;

  const countryStore = useUnit($countriesStore.store);
  const mandatoryFields = useUnit($recipientMandatoryFieldsStore.store);
  const countryProvincesStore = useUnit($countryProvincesStore.store);

  const { t } = useTranslation('payments');

  const { control, watch, setValue } = useFormContext<AddressFormData>();
  const companyCountry = watch('companyCountry');

  const provinceOptions = useMemo(
    () => (companyCountry in countryProvincesStore.data ? countryProvincesStore.data[companyCountry] : []),
    [companyCountry, countryProvincesStore.data],
  );
  const countryOptions = useMemo(() => countryStore.data.map((country) => ({ label: country.name, value: country.twoLetterCode })), [countryStore]);
  return (
    <div className={cn('flex flex-col gap-2', props.className)}>
      <Text className={cn(PaletteUtils.isVcfcPalette && 'dark:text-white/50')}>{t('form.placeholders.address')}</Text>
      {mandatoryFields.data.includes('insi_address') && (
        <Controller
          name='address'
          control={control}
          rules={{
            required: t('errors.form.required', { ns: 'common' }),
            maxLength: { value: 100, message: t('errors.form.maxCharacters', { ns: 'common', max: 100 }) },
            pattern: {
              value: ValidateUtils.alphaNumericRegex,
              message: t('errors.form.alphaNumericFormat', { ns: 'common', field: t('form.placeholders.address') }),
            },
          }}
          render={({ field, fieldState: { error } }) => (
            <BaseInput
              {...field}
              label={t('form.placeholders.address')}
              placeholder={t('form.placeholders.address')}
              required
              error={error?.message}
            />
          )}
        />
      )}
      {mandatoryFields.data.includes('insi_addresspostcode') && (
        <Controller
          name='postCode'
          control={control}
          rules={{
            required: t('errors.form.required', { ns: 'common' }),
            maxLength: { value: 10, message: t('errors.form.maxCharacters', { ns: 'common', max: 10 }) },
            pattern: {
              value: ValidateUtils.alphaNumericRegex,
              message: t('errors.form.alphaNumericFormat', { ns: 'common', field: t('form.placeholders.zipPostCode') }),
            },
          }}
          render={({ field, fieldState: { error } }) => (
            <BaseInput
              {...field}
              label={t('form.placeholders.zipPostCode')}
              placeholder={t('form.placeholders.zipPostCode')}
              required
              maxLength={10}
              error={error?.message}
            />
          )}
        />
      )}
      {mandatoryFields.data.includes('insi_addresscity') && (
        <Controller
          name='city'
          control={control}
          rules={{
            required: t('errors.form.required', { ns: 'common' }),
            maxLength: { value: 100, message: t('errors.form.maxCharacters', { ns: 'common', max: 100 }) },
            pattern: {
              value: ValidateUtils.alphaNumericRegex,
              message: t('errors.form.alphaNumericFormat', { ns: 'common', field: t('form.placeholders.townCity') }),
            },
          }}
          render={({ field, fieldState: { error } }) => (
            <BaseInput
              {...field}
              label={t('form.placeholders.townCity')}
              placeholder={t('form.placeholders.townCity')}
              required
              error={error?.message}
            />
          )}
        />
      )}
      {mandatoryFields.data.includes('insi_addressprovince') && (
        <Controller
          control={control}
          name='province'
          rules={{ required: t('errors.form.required', { ns: 'common' }) }}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <Select
              wrapperClassName='overflow-hidden'
              buttonClassName='bg-transparent '
              iconClassName='!text-primary dark:!text-white'
              enableSearch
              label={t('form.placeholders.provinceRegion')}
              nullValue={{ isEnable: true, text: t('form.placeholders.provinceRegion') }}
              items={provinceOptions}
              required
              error={error?.message}
              value={value}
              onChange={onChange}
            />
          )}
        />
      )}
      <Controller
        control={control}
        name='companyCountry'
        rules={{ required: t('errors.form.required', { ns: 'common' }) }}
        render={({ field: { value, onChange }, fieldState: { error } }) => (
          <Select
            wrapperClassName='overflow-hidden'
            buttonClassName='bg-transparent '
            iconClassName='!text-primary dark:!text-white'
            enableSearch
            label={t(`form.placeholders.${type === TransferRecipientTypeEnum.Individual ? 'beneficiaryResidentialCountry' : 'incorporationCountry'}`)}
            nullValue={{
              isEnable: true,
              text: t(
                `form.placeholders.${type === TransferRecipientTypeEnum.Individual ? 'beneficiaryResidentialCountry' : 'incorporationCountry'}`,
              ),
            }}
            items={countryOptions}
            required
            error={error?.message}
            value={value}
            onChange={(e) => (onChange(e), setValue('province', ''))}
          />
        )}
      />
    </div>
  );
};
